// Central content source. Editable structural content for the marketing site.
// Programmes, stories, reports and impact figures are managed via the backend/admin.

export const MAIN_NAV = [
  {
    label: "About",
    to: "/about",
    children: [
      { label: "About the Foundation", to: "/about" },
      { label: "Our Focus", to: "/focus" },
      { label: "Our People", to: "/people" },
      { label: "Impact", to: "/impact" },
      { label: "Transparency", to: "/transparency" },
    ],
  },
  {
    label: "Our Work",
    to: "/programmes",
    children: [
      { label: "Programmes", to: "/programmes" },
      { label: "Project Dawn", to: "/dawn" },
      { label: "Where We Work", to: "/where-we-work" },
      { label: "Stories", to: "/stories" },
    ],
  },
  {
    label: "Expeditions & Films",
    to: "/expeditions",
    children: [
      { label: "Expeditions", to: "/expeditions" },
      { label: "Films", to: "/films" },
    ],
  },
  {
    label: "Learn & Train",
    to: "/learning",
    children: [
      { label: "Training", to: "/learning/training" },
      { label: "Volunteer", to: "/volunteer" },
      { label: "Field Experiences", to: "/learning/field" },
      { label: "Learning Pathways", to: "/learning/pathways" },
      { label: "Certificates", to: "/verify" },
      { label: "Upcoming Programmes", to: "/learning/upcoming" },
      { label: "Institutional Training", to: "/learning/institutional" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

export const FOOTER_NAV = [
  { label: "About", to: "/about" },
  { label: "Our Focus", to: "/focus" },
  { label: "Programmes", to: "/programmes" },
  { label: "Project Dawn", to: "/dawn" },
  { label: "Our People", to: "/people" },
  { label: "Where We Work", to: "/where-we-work" },
  { label: "Expeditions & Films", to: "/expeditions" },
  { label: "Films", to: "/films" },
  { label: "Learning & Training", to: "/learning" },
  { label: "Impact", to: "/impact" },
  { label: "Partnerships", to: "/partnerships" },
  { label: "Stories", to: "/stories" },
  { label: "Transparency", to: "/transparency" },
  { label: "Volunteer", to: "/volunteer" },
  { label: "Donate", to: "/donate" },
  { label: "Contact", to: "/contact" },
];

export const PILLARS = [
  {
    num: "01",
    title: "Conservation & Biodiversity",
    slug: "conservation-biodiversity",
    short:
      "Protecting ecosystems, wildlife and the natural systems that sustain life.",
    description:
      "Environmental conservation, wildlife protection, biodiversity, ecological restoration, afforestation and reforestation, habitat conservation, forests, rivers, wetlands, water conservation, climate resilience and sustainable resource management.",
    intended:
      "Our objects cover the protection and restoration of India's forests, rivers, wetlands and wildlife habitats, together with the long, patient work of climate resilience.",
    directions: [
      "Habitat and forest restoration approaches",
      "Wetland and river ecosystem protection",
      "Community-led afforestation initiatives",
      "Biodiversity monitoring and climate resilience",
    ],
    partnerships:
      "Forest departments, conservation NGOs, research institutions and CSR partners with environmental mandates.",
    image:
      "https://images.pexels.com/photos/15345428/pexels-photo-15345428.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  },
  {
    num: "02",
    title: "Responsible Tourism",
    slug: "responsible-tourism",
    short:
      "A philosophy where exploration supports conservation and community.",
    description:
      "Responsible tourism, eco-tourism, nature-based tourism, cultural and heritage tourism, conservation tourism and community-based adventure.",
    intended:
      "Travel in India, done carefully, can fund conservation, sustain living heritage and keep income in the villages that host it. This is a philosophy of responsible exploration, not a tourism booking business.",
    directions: [
      "Frameworks for low-impact nature experiences",
      "Community-based and conservation tourism models",
      "Cultural and heritage preservation approaches",
      "Guidelines for responsible exploration",
    ],
    partnerships:
      "Local communities, tourism boards, conservation bodies and responsible enterprise partners.",
    image: "/expeditions/valley-trekkers.jpg",
  },
  {
    num: "03",
    title: "Communities & Livelihoods",
    slug: "communities-livelihoods",
    short:
      "Building resilient rural economies and dignified opportunity.",
    description:
      "Livelihood generation, vocational training, entrepreneurship, self-employment, women empowerment, farmer welfare, tribal welfare, youth development, indigenous knowledge and rural development.",
    intended:
      "Sustainable livelihoods, enterprise and rural development sit at the centre of our objects, with particular attention to women, farmers, tribal communities and young people.",
    directions: [
      "Vocational training and skilling approaches",
      "Women-led enterprise and self-employment",
      "Farmer and tribal welfare initiatives",
      "Preserving indigenous knowledge systems",
    ],
    partnerships:
      "Community organisations, self-help groups, skilling institutions and CSR livelihood programmes.",
    image:
      "https://images.pexels.com/photos/20356768/pexels-photo-20356768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  },
  {
    num: "04",
    title: "Education & Knowledge",
    slug: "education-knowledge",
    short:
      "Learning, research and capacity building for a sustainable future.",
    description:
      "Environmental education, vocational education, digital inclusion, scientific research, workshops, seminars, training, capacity building and knowledge sharing.",
    intended:
      "Our objects include environmental and vocational education, digital inclusion and applied research. Whatever we learn will be published so that others can use it.",
    directions: [
      "Environmental education curricula",
      "Digital inclusion and access programmes",
      "Applied research and knowledge sharing",
      "Workshops, training and capacity building",
    ],
    partnerships:
      "Universities, schools, research institutions and educational foundations.",
    image:
      "https://images.pexels.com/photos/15119089/pexels-photo-15119089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  },
  {
    num: "05",
    title: "Sustainable Agriculture",
    slug: "sustainable-agriculture",
    short:
      "Regenerative farming that restores soil, water and rural life.",
    description:
      "Sustainable and regenerative agriculture, organic farming, agroforestry, permaculture and allied rural development.",
    intended:
      "Regenerative and organic farming can restore soil and water while strengthening rural incomes. That is the standard our agricultural work will be designed around.",
    directions: [
      "Regenerative and organic farming models",
      "Agroforestry and permaculture approaches",
      "Soil and water health initiatives",
      "Allied rural development programmes",
    ],
    partnerships:
      "Agricultural universities, farmer collectives, and sustainable food-system partners.",
    image:
      "https://images.unsplash.com/photo-1772512799691-004541bc2f7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    num: "06",
    title: "Health, Water & Wellbeing",
    slug: "health-water-wellbeing",
    short:
      "Access to clean water, healthcare and community infrastructure.",
    description:
      "Healthcare, wellness, drinking water, sanitation, water conservation and community-oriented infrastructure.",
    intended:
      "Safe drinking water, sanitation, healthcare access and community infrastructure, designed around what each place actually needs rather than a single template.",
    directions: [
      "Safe drinking water and sanitation approaches",
      "Community health and wellness initiatives",
      "Water conservation infrastructure",
      "Community-oriented public infrastructure",
    ],
    partnerships:
      "Health institutions, local bodies, water-focused NGOs and CSR health programmes.",
    image:
      "https://images.pexels.com/photos/36110365/pexels-photo-36110365.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  },
  {
    num: "07",
    title: "Youth & Sports",
    slug: "youth-sports",
    short:
      "Developing potential through sport, recreation and outdoor life.",
    description:
      "Youth development, sports development, recreation, sports facilities and capacity building.",
    intended:
      "Sport, recreation and time outdoors build confidence and discipline in young people. Youth and sports development is written into the Foundation's objects for that reason.",
    directions: [
      "Youth development and mentorship",
      "Sports and recreation access",
      "Outdoor and adventure-based learning",
      "Facility and capacity building",
    ],
    partnerships:
      "Sports bodies, educational institutions, and youth-focused CSR partners.",
    image:
      "https://images.pexels.com/photos/33874843/pexels-photo-33874843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  },
  {
    num: "08",
    title: "Innovation & Technology",
    slug: "innovation-technology",
    short:
      "Data, AI and sustainable technology for social and environmental impact.",
    description:
      "Sustainable technology, environmental technology, artificial intelligence, data-driven systems, research and innovation for social and environmental impact.",
    intended:
      "Sustainable technology, data systems and AI can make conservation and community work more precise. We will use them where they serve the work, not for their own sake.",
    directions: [
      "Environmental monitoring and data systems",
      "AI for conservation and impact",
      "Sustainable and clean technology",
      "Research-led innovation",
    ],
    partnerships:
      "Technology partners, research labs, universities and innovation-focused foundations.",
    image:
      "https://images.unsplash.com/photo-1756507505177-e9c540e8dd67?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
];

export const TOURISM_PRINCIPLES = [
  { num: "01", title: "Respect Nature", text: "Exploration that protects, never depletes, the ecosystems it enters." },
  { num: "02", title: "Respect Communities", text: "Local people are partners in tourism, not a backdrop to it." },
  { num: "03", title: "Preserve Culture", text: "Heritage, tradition and identity carried forward with dignity." },
  { num: "04", title: "Create Responsible Opportunity", text: "Economic value that stays in the places that create it." },
];

export const APPROACH = [
  { num: "01", title: "Understand", text: "Identify ecological and community needs." },
  { num: "02", title: "Design", text: "Develop locally relevant approaches." },
  { num: "03", title: "Collaborate", text: "Work with communities, experts and institutions." },
  { num: "04", title: "Implement", text: "Deliver responsible programmes." },
  { num: "05", title: "Measure", text: "Document outcomes and learning." },
  { num: "06", title: "Scale", text: "Expand what the evidence supports." },
];

export const COMMUNITY_AREAS = [
  { title: "Livelihoods & Enterprise", image: "https://images.pexels.com/photos/11070641/pexels-photo-11070641.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700" },
  { title: "Women & Farmers", image: "https://images.pexels.com/photos/20356768/pexels-photo-20356768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700" },
  { title: "Tribal & Youth Communities", image: "https://images.pexels.com/photos/15119089/pexels-photo-15119089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700" },
  { title: "Sustainable Agriculture", image: "https://images.unsplash.com/photo-1772512799691-004541bc2f7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
];

export const IMPACT_DIMENSIONS = [
  { key: "PLANET", title: "Planet", text: "Forests, rivers, wetlands and wildlife: ecosystems protected, restored and monitored." },
  { key: "PEOPLE", title: "People", text: "Livelihoods, health, education and opportunity for communities in India and beyond." },
  { key: "KNOWLEDGE", title: "Knowledge", text: "Research, learning and open documentation that others can build upon." },
  { key: "RESILIENCE", title: "Resilience", text: "Communities and ecosystems better able to withstand a changing climate." },
];

export const PARTNERSHIP_CATEGORIES = [
  { title: "CSR Partnerships", text: "Corporate teams seeking credible, measurable environmental and social impact aligned with CSR mandates." },
  { title: "Conservation Partnerships", text: "Forest departments, conservation bodies and NGOs working to protect ecosystems and wildlife." },
  { title: "Research Partnerships", text: "Universities and research institutions advancing science, data and evidence for impact." },
  { title: "Educational Partnerships", text: "Schools, colleges and educational foundations building environmental and vocational learning." },
  { title: "Community Partnerships", text: "Community organisations, self-help groups and local stakeholders leading change on the ground." },
  { title: "Institutional Partnerships", text: "Government authorities, local bodies, foundations and international organisations." },
];

export const PARTNER_TYPES = [
  "Government / Local Body",
  "Corporation / CSR Team",
  "NGO / Trust",
  "University / Research Institution",
  "Educational Institution",
  "Foundation",
  "International Organisation",
  "Community Stakeholder",
  "Individual Supporter",
  "Other",
];

export const CONTACT_CATEGORIES = [
  "General Enquiry",
  "Partnership",
  "CSR",
  "Programme Collaboration",
  "Research",
  "Media",
  "Volunteering",
];

export const STORY_CATEGORIES = [
  "Conservation",
  "Responsible Exploration",
  "Communities",
  "Agriculture",
  "Education",
  "Research",
  "Youth",
  "Sustainability",
];

export const PROGRAMME_CATEGORIES = [
  "Conservation",
  "Responsible Tourism",
  "Communities",
  "Education",
  "Livelihoods",
  "Agriculture",
  "Health & Water",
  "Youth & Sports",
  "Innovation",
  "Conservation & Biodiversity",
  "Sustainable Agriculture & Livelihoods",
  "Digital Inclusion & Technology",
  "Women & Community Enterprise",
  "Sustainable Tourism & Community Development",
  "Water & Climate Resilience",
  "Circular Economy & Waste",
  "Disaster Preparedness & Community Resilience",
  "Education & Skills",
  "Health & Community Awareness",
];

export const PROGRAMME_STATUSES = ["CONCEPT", "IN DEVELOPMENT", "PILOT", "ACTIVE", "COMPLETED", "PAUSED", "ARCHIVED"];

export const PROGRAMME_STATUS_LABEL = {
  CONCEPT: "Concept / Proposed Programme",
  "IN DEVELOPMENT": "Programme in Development",
  PILOT: "Pilot Programme",
  ACTIVE: "Active Programme",
  COMPLETED: "Completed",
  PAUSED: "Paused",
  ARCHIVED: "Archived",
};

export const SDG_LIST = [
  { num: 1, title: "No Poverty" },
  { num: 2, title: "Zero Hunger" },
  { num: 3, title: "Good Health & Well-Being" },
  { num: 4, title: "Quality Education" },
  { num: 5, title: "Gender Equality" },
  { num: 6, title: "Clean Water & Sanitation" },
  { num: 7, title: "Affordable & Clean Energy" },
  { num: 8, title: "Decent Work & Economic Growth" },
  { num: 9, title: "Industry, Innovation & Infrastructure" },
  { num: 10, title: "Reduced Inequalities" },
  { num: 11, title: "Sustainable Cities & Communities" },
  { num: 12, title: "Responsible Consumption & Production" },
  { num: 13, title: "Climate Action" },
  { num: 14, title: "Life Below Water" },
  { num: 15, title: "Life on Land" },
  { num: 16, title: "Peace, Justice & Strong Institutions" },
  { num: 17, title: "Partnerships for the Goals" },
];
export const SDG_TITLE = SDG_LIST.reduce((acc, s) => { acc[s.num] = s.title; return acc; }, {});

export const PARTNERSHIP_REQUIREMENT_TYPES = [
  "CSR Funding",
  "Technology",
  "Equipment",
  "Research",
  "Training",
  "Technical Expertise",
  "Community Implementation",
  "Government Collaboration",
  "Academic Partnership",
  "Infrastructure",
  "Documentation",
  "Film / Media",
  "Field Support",
  "Wildlife Expertise",
  "Veterinary Support",
  "Field Equipment",
  "Community Awareness",
  "Conservation Organisations",
  "Authorised Rescue / Rehabilitation Organisations",
];

// ---- Project Dawn (source: supplied Project Dawn field documents) ----

export const DAWN_CHALLENGES = [
  "Migration away from the villages",
  "Basic amenities",
  "Women's hygiene",
  "Education",
  "Electricity",
  "Access to hospitals",
  "Means of daily wages",
  "Water scarcity",
  "Connectivity to the nearby village and city",
  "Jobs",
];

export const DAWN_MODEL = [
  { num: "01", title: "Understand the community", text: "Live in the place. Listen before planning." },
  { num: "02", title: "Identify opportunities", text: "What the landscape, culture and people already offer." },
  { num: "03", title: "Connect people & places", text: "Bring travellers, volunteers and skills to the valley." },
  { num: "04", title: "Develop experiences", text: "Treks, rivers, homestays and village life, designed responsibly." },
  { num: "05", title: "Train & build capacity", text: "Survival, wilderness, river and hosting skills for local people." },
  { num: "06", title: "Create local opportunity", text: "Homestays, local guiding, new farming techniques, digital literacy." },
  { num: "07", title: "Generate revenue", text: "Income that stays in the villages that host the work." },
  { num: "08", title: "Strengthen the community", text: "A place people can stay in, not one they must leave." },
];

export const DAWN_SDGS = [
  { num: 1, title: "No Poverty", link: "Local income through homestays and guiding" },
  { num: 2, title: "Zero Hunger", link: "New farming techniques introduced to villagers" },
  { num: 3, title: "Good Health & Well-Being", link: "Medicines distributed; healthcare access documented" },
  { num: 4, title: "Quality Education", link: "Stationery distributed; first computer access in nine years" },
  { num: 5, title: "Gender Equality", link: "Women's hygiene: pads distributed in the villages" },
  { num: 6, title: "Clean Water & Sanitation", link: "Hand pump installed; water scarcity documented" },
  { num: 7, title: "Affordable & Clean Energy", link: "1.5 kW solar panel installed; generator repaired" },
  { num: 11, title: "Sustainable Cities & Communities", link: "Rural tourism pilot across three adopted villages" },
  { num: 13, title: "Climate Action", link: "Tree plantation and environmental education" },
  { num: 14, title: "Life Below Water", link: "River-based programmes with clean-river principles" },
  { num: 15, title: "Life on Land", link: "450 trees planted; wildlife expeditions in the Nandhaur range" },
];

export const DAWN_TEAM = [
  { name: "Nikhil Pratap Singh", role: "Founder · Expedition Leader", documented: true, photo: "/dawn/kilimanjaro-summit.jpg" },
  { name: "Raj Garkoti", role: "Field team", documented: false, photo: "/team/raj-garkoti.png" },
];

export const DAWN_TIMELINE = [
  { period: "2015", title: "Into the field", text: "Nikhil graduates, leaves a desk job within four months, and goes full-time into mountaineering, kayaking, rafting, surfing and wilderness training." },
  { period: "2019", title: "The valley is found", text: "A recce to open a rafting route in remote Uttarakhand finds three villages living without roads, electricity or hospitals. Project Dawn begins, and with it the high-altitude mission, filmed for a documentary." },
  { period: "2020", title: "Valley of River begins", text: "Three villages adopted in the Ladhiya Valley, Uttarakhand. The first programme runs in December 2020, before further editions in 2021 and 2023." },
  { period: "2020-2023", title: "Community interventions", text: "Solar panel, hand pump, repairs, medicines, pads, stationery and first computer access, documented in the field reports." },
  { period: "2023", title: "Kilimanjaro summited", text: "Uhuru Peak, 5,895 m: the first of the Seven Summits climbed under the mission. The remaining peaks, and the 8000-metre giants, await partners and sponsors." },
  { period: "Feb 2024", title: "450 trees, one field day", text: "A plantation drive with volunteers in Village Kuna, Tiuni, Uttarakhand." },
  { period: "Ongoing", title: "Butt to Board", text: "Cigarette-butt collection across four cities and a handmade surfboard built from the waste." },
  { period: "2026", title: "The next chapter", text: "The Last Horizon Wildlife Filmmaking Challenge, the Dawn film and the formalisation of learning & training." },
];

export const DAWN_TARGETS = [
  "50+ villages",
  "50+ schools",
  "50+ homestays",
  "50+ village children in outdoor sport",
  "50+ outdoor sports camps",
  "100+ villages in the longer term",
];

export const FOUR_WAYS = [
  { key: "adventures", title: "Planet Adventures", text: "Outdoor and adventure sports in wild places: trekking, rafting, kayaking, surfing and the leadership they build.", image: "/dawn/vor-rafting.jpg", to: "/exploration" },
  { key: "wildlife", title: "Planet Wildlife", text: "Wildlife expeditions and documentation, from the Nandhaur range to the plains of Tanzania.", image: "/dawn/nandhaur-poster.jpg", to: "/flagship" },
  { key: "dawn", title: "Planet Dawn", text: "The Dawn Series: field-based community development where adventure meets rural India.", image: "/dawn/vor-family.jpg", to: "/dawn" },
  { key: "expeditions", title: "Planet Expeditions", text: "Long-format journeys to unusual, extreme and remote locations, documented as they happen.", image: "/dawn/kilimanjaro-summit.jpg", to: "/exploration" },
];

export const EXPLORE_FRAMEWORK = [
  { num: "01", title: "Explore", text: "Go to the place. On foot, on the river, on the mountain." },
  { num: "02", title: "Understand", text: "Meet the people and the problems where they live." },
  { num: "03", title: "Participate", text: "Volunteer, document, plant, teach, repair." },
  { num: "04", title: "Learn", text: "Wilderness, survival, river and leadership skills." },
  { num: "05", title: "Create opportunity", text: "Homestays, guiding, training and local income." },
  { num: "06", title: "Impact", text: "Documented in the field, never claimed in advance." },
];

export const IMPACT_CATEGORIES = ["Communities", "Education", "Health & Hygiene", "Clean Energy", "Water", "Environment", "Rural Tourism", "Adventure & Sports", "Wildlife", "Livelihoods"];

export const IMPACT_STATUSES = ["DOCUMENTED", "HISTORICAL", "PILOT", "PLANNED", "VERIFY"];

// value = geojson st_nm (for map matching), label = display name
export const INDIA_STATES = [
  { value: "Andaman and Nicobar Islands", label: "Andaman & Nicobar Islands" },
  { value: "Andhra Pradesh", label: "Andhra Pradesh" },
  { value: "Arunachal Pradesh", label: "Arunachal Pradesh" },
  { value: "Assam", label: "Assam" },
  { value: "Bihar", label: "Bihar" },
  { value: "Chhattisgarh", label: "Chhattisgarh" },
  { value: "Goa", label: "Goa" },
  { value: "Gujarat", label: "Gujarat" },
  { value: "Haryana", label: "Haryana" },
  { value: "Himachal Pradesh", label: "Himachal Pradesh" },
  { value: "Jammu and Kashmir", label: "Jammu & Kashmir" },
  { value: "Jharkhand", label: "Jharkhand" },
  { value: "Karnataka", label: "Karnataka" },
  { value: "Kerala", label: "Kerala" },
  { value: "Ladakh", label: "Ladakh" },
  { value: "Lakshadweep", label: "Lakshadweep" },
  { value: "Madhya Pradesh", label: "Madhya Pradesh" },
  { value: "Maharashtra", label: "Maharashtra" },
  { value: "Manipur", label: "Manipur" },
  { value: "Meghalaya", label: "Meghalaya" },
  { value: "Mizoram", label: "Mizoram" },
  { value: "Nagaland", label: "Nagaland" },
  { value: "Orissa", label: "Odisha" },
  { value: "Punjab", label: "Punjab" },
  { value: "Rajasthan", label: "Rajasthan" },
  { value: "Sikkim", label: "Sikkim" },
  { value: "Tamil Nadu", label: "Tamil Nadu" },
  { value: "Tripura", label: "Tripura" },
  { value: "Uttar Pradesh", label: "Uttar Pradesh" },
  { value: "Uttaranchal", label: "Uttarakhand" },
  { value: "West Bengal", label: "West Bengal" },
  { value: "Delhi", label: "Delhi" },
  { value: "Puducherry", label: "Puducherry" },
];

export const STATE_LABEL = INDIA_STATES.reduce((acc, s) => { acc[s.value] = s.label; return acc; }, {});

export const JOURNEY_FORMATS = [
  { key: "TRAIL", label: "Trails", days: "1-3 days", blurb: "Short field immersions close to home: one landscape, its people and its questions." },
  { key: "QUEST", label: "Quests", days: "4-7 days", blurb: "A week inside one landscape, learning and documenting alongside the communities who live there." },
  { key: "EXPEDITION", label: "Expeditions", days: "8+ days", blurb: "Long-format field journeys, including our documentary-led flagship expeditions." },
];

export const FORMAT_LABEL = { TRAIL: "Trail", QUEST: "Quest", EXPEDITION: "Expedition" };

export const EXPERIENCE_CATEGORIES = [
  "Wildlife",
  "Conservation",
  "Photography",
  "Documentary",
  "Adventure",
  "Research",
  "Community",
  "Expedition",
];

export const TRAINING_THEMES = [
  {
    group: "Photography & Documentation",
    note: "The craft of seeing and recording the natural world responsibly.",
    courses: ["Wildlife Photography", "Conservation Photography", "Field Documentation", "Wildlife Ethics"],
  },
  {
    group: "Expedition & Outdoor Skills",
    note: "The disciplines that keep field teams safe and self-reliant.",
    courses: ["Outdoor Leadership", "Expedition Skills", "Mountain Safety"],
  },
  {
    group: "Conservation Science",
    note: "Understanding ecosystems well enough to help protect them.",
    courses: ["Biodiversity", "Field Research", "Conservation Monitoring"],
  },
];

const COUNTRY_CONTINENT = {
  India: "Asia", Nepal: "Asia", Bhutan: "Asia", "Sri Lanka": "Asia", Indonesia: "Asia", Thailand: "Asia", Mongolia: "Asia",
  Tanzania: "Africa", Kenya: "Africa", Uganda: "Africa", Rwanda: "Africa", Botswana: "Africa", Namibia: "Africa",
  "South Africa": "Africa", Zambia: "Africa", Zimbabwe: "Africa", Madagascar: "Africa", Ethiopia: "Africa",
};

export const experiencePlace = (j) => {
  const c = j.country || (j.region ? "India" : "");
  return [c, COUNTRY_CONTINENT[c] || ""].filter(Boolean).join(" · ");
};

export const VOLUNTEER_AREAS = [
  "Conservation & Fieldwork",
  "Responsible Tourism",
  "Education & Mentoring",
  "Community Development",
  "Research & Documentation",
  "Photography & Film",
  "Design & Communications",
  "Technology & Data",
];

export const fmtJourneyDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

export const locationLabel = (x) => {
  const country = x.country || (x.region ? "India" : "");
  const region = STATE_LABEL[x.region] || x.region;
  const alreadyMentioned = region && x.location && (x.location.toLowerCase().includes(region.toLowerCase()) || x.location.toLowerCase().includes("india"));
  const showRegion = region && (!x.country || x.country === "India") && !alreadyMentioned;
  return [x.location, showRegion ? region : "", country && country !== "India" ? country : ""].filter(Boolean).join(", ");
};

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1504252060324-1c76e2e09939?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  river: "https://images.unsplash.com/photo-1772259578049-05f606860454?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  bigIdea: "https://images.pexels.com/photos/33235130/pexels-photo-33235130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000",
  tourism: "/expeditions/valley-trekkers.jpg",
  forest: "https://images.pexels.com/photos/33235130/pexels-photo-33235130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1400",
  aboutHero: "https://images.pexels.com/photos/13768017/pexels-photo-13768017.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=2000",
};

export const WORLD_COUNTRIES = ["Afghanistan", "Albania", "Algeria", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bangladesh", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herz.", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Central African Rep.", "Chad", "Chile", "China", "Colombia", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia", "Côte d'Ivoire", "Dem. Rep. Congo", "Denmark", "Djibouti", "Dominican Rep.", "Ecuador", "Egypt", "El Salvador", "Eq. Guinea", "Eritrea", "Estonia", "Ethiopia", "Falkland Is.", "Fiji", "Finland", "Fr. S. Antarctic Lands", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Greenland", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Lithuania", "Luxembourg", "Macedonia", "Madagascar", "Malawi", "Malaysia", "Mali", "Mauritania", "Mexico", "Moldova", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "N. Cyprus", "Namibia", "Nepal", "Netherlands", "New Caledonia", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "Norway", "Oman", "Pakistan", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Puerto Rico", "Qatar", "Romania", "Russia", "Rwanda", "S. Sudan", "Saudi Arabia", "Senegal", "Serbia", "Sierra Leone", "Slovakia", "Slovenia", "Solomon Is.", "Somalia", "Somaliland", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States of America", "Uruguay", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam", "W. Sahara", "Yemen", "Zambia", "Zimbabwe", "eSwatini"];
